import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { SponsorDto } from "../../../models/sponsor/dto/sponsor.dto";
import { CreateSponsorInputDto } from "../../../models/sponsor/dto/create-sponsor.dto";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";

export type CreateSponsorWithLogoInput = {
    sponsorData: Omit<CreateSponsorInputDto, 'logoUrl'>;
    logoFile?: File;
};

export const useCreateSponsor = () => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationFn: (input: CreateSponsorInputDto | CreateSponsorWithLogoInput) => {
            const organizationId = selectedOrganization?.id;
            if (!organizationId) {
                throw new Error('No organization selected');
            }
            // Check if it's the new format with logo file
            if ('sponsorData' in input) {
                const formData = new FormData();
                const sponsorData = { ...input.sponsorData, organizationId };

                // Append all sponsor data fields
                Object.entries(sponsorData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        formData.append(key, value.toString());
                    }
                });

                // Append logo file if provided
                if (input.logoFile) {
                    formData.append('logo', input.logoFile);
                }

                return client.fetchApiWithFormData<SponsorDto>({
                    endpoint: 'sponsors/with-logo',
                    method: 'POST',
                    formData
                });
            } else {
                // Legacy format - regular JSON request
                return client.fetchApi<SponsorDto, CreateSponsorInputDto>({
                    endpoint: 'sponsors',
                    method: 'POST',
                    body: { ...input, organizationId }
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sponsors', 'list', selectedOrganization?.id || ''] });
        },
    });
};
